import React, { useState } from "react"

import { joinClasses, changeView, useClasses } from "../globals";
import { resetPassword, changePassword } from "../simgate";

import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"

// Icons
import AutorenewIcon from "@material-ui/icons/Autorenew"
import VisibilityIcon from "@material-ui/icons/Visibility"
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff"
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"
import { Check, Close } from "@material-ui/icons"
import { action, makeAutoObservable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import { passwordConditions, PasswordHint } from "./password-hint";

let token = "";

class ChangePasswordStore {

  processing = false;

  password = "";
  passwordError = "";
  passwordVisible = false;

  passwordConfirm = "";
  passwordConfirmError = "";

  serverError = "";

  constructor(app) {
    makeAutoObservable(this);
    this.app = app;

    
    const url = new URL(window.location.href);
    if (url.searchParams.has("passwordResetToken")) {
      token = url.searchParams.get("passwordResetToken");
      url.searchParams.delete("passwordResetToken");
      window.history.replaceState({}, "Enterprise Dashboard", url.href);
    }
  }

  async changePassword() {

    let cont = true;
    const {
      password,
      passwordConfirm
    } = this;

    runInAction(() => {
      this.processing = true;
      this.passwordError = "";
      this.passwordConfirmError = "";
      this.serverError = "";

      if (!password) {
        this.passwordError = "Password is required";
      }
      else if (!passwordConditions.every(item => item.condition(password))) {
        this.passwordError = "Password does not meet all the requirements";
      }
      if (password !== passwordConfirm) {
        this.passwordConfirmError = "Passwords do not match";
      }
      if (this.passwordError || this.passwordConfirmError) {
        this.processing = false;
        cont = false;
      }
    });

    if (!cont) {
      return;
    }

    try {
      const [result] = await resetPassword({
        token,
        password
      });

      runInAction(() => {
        this.processing = false;
        if (result.error) {
          console.log(result.message);
          this.serverError = "An unknown error occured while attempting to reset your password. Please try again.";
        }
        else {
          console.log("Successfully reset");
          changeView("ChangePasswordComplete");
        }
      });
    }
    catch (e) {
      console.error(e);
      runInAction(() => {
        this.processing = false;
        this.serverError = "An unknown error occured while attempting to reset your password. Please try again.";
      });
    }
  }
}

export const ChangePassword = observer(({ app }) => {
  const classes = useClasses()
  const [store] = useState(() => new ChangePasswordStore(app));
  const {
    password,
    passwordError,
    passwordConfirm,
    passwordConfirmError,
    passwordVisible,
    processing,
    serverError
  } = store;

  return (
    <React.Fragment>
      <Box
        className={joinClasses(
          classes._Padding,
          classes._Flex,
          classes._FlexDirectionColumn
        )}
      >
        <Collapse in>
          <Box className={classes._Margin}>
            <Alert severity="warning">
              <Typography>
                Please enter your new password.
              </Typography>
            </Alert>
          </Box>
        </Collapse>

        <Box className={classes._Margin}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled={processing}
            label="Password"
            type={passwordVisible ? "text" : "password"}
            value={password}
            onFocus={action(() => {
              store.serverError = false;
              store.passwordError = false;
            })}
            onChange={action(event => store.password = event.target.value)}
            onKeyPress={(e) => { if (e.charCode === 13) { store.changePassword() } }}
            error={Boolean(passwordError)}
            helperText={passwordError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={action(() => store.passwordVisible = !passwordVisible)}
                    edge="end"
                    tabIndex="-1"
                  >
                    {passwordVisible ? (<VisibilityOffIcon />) : (<VisibilityIcon />)}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Box className={classes._Margin}>
          <PasswordHint password={password} />
        </Box>

        <Box className={classes._Margin}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled={processing}
            label="Password confirm"
            type="password"
            value={passwordConfirm}
            onFocus={action(() => {
              store.serverError = false;
              store.passwordConfirmError = false;
            })}
            onChange={action(event => store.passwordConfirm = event.target.value)}
            onKeyPress={(e) => { if (e.charCode === 13) { store.changePassword() } }}
            error={Boolean(passwordConfirmError)}
            helperText={passwordConfirmError}
          />
        </Box>

        <Collapse in={Boolean(serverError)}>
          <Box mb={2}>
            <Alert severity="error">
              <Typography>{serverError}</Typography>
            </Alert>
          </Box>
        </Collapse>

        <Box className={classes._Margin}>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            type="submit"
            startIcon={
              processing ? (
                <AutorenewIcon className={classes.processingIcon} />
              ) : null
            }
            onClick={() => store.changePassword()}
            disabled={processing}
          >
            Reset password
          </Button>
        </Box>
      </Box>

      <Divider />

      <Box className={classes._Padding}>
        <Box className={classes._Margin}>
          <Button
            variant="text"
            fullWidth
            disabled={processing}
            onClick={() => changeView("EmailPasswordLogin")}
            startIcon={<ChevronLeftIcon />}
          >
            Back to Sign in
          </Button>
        </Box>
      </Box>
    </React.Fragment>
  )
})